/**
 * MfaSetupPage — Iter 155 · HIPAA MFA Enforcement.
 *
 * Blocks all other navigation for admin + clinician users whose role
 * requires MFA but who haven't enrolled yet. The user arrives here
 * with a scoped ``mfa_setup_only`` JWT that unlocks ONLY the
 * ``/api/auth/mfa/*`` endpoints. Escape hatches:
 *   1. Complete enrollment (QR + 6-digit TOTP code → backup codes)
 *   2. Sign out (clears the scoped token and returns to /auth)
 *
 * On successful enrollment the backend upgrades the scoped token to a
 * full session token; AuthContext.completeMfaSetup() swaps it in and
 * navigation to the role dashboard proceeds normally.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, KeyRound, Copy, Download, ArrowRight, LogOut, Loader2, Check,
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_HOME = {
  admin: '/admin/dashboard',
  clinician: '/provider-os',
  agency: '/provider-os',
  discharge_planner: '/planner/dashboard',
  hospital_admin: '/planner/dashboard',
  payer: '/payer/dashboard',
  patient: '/patient/dashboard',
};

export default function MfaSetupPage() {
  const navigate = useNavigate();
  const {
    mfaSetupToken, mfaSetupUser,
    getMfaSetupAuthHeader, completeMfaSetup,
    logout,
  } = useAuth();

  const [phase, setPhase] = useState('loading'); // loading | setup | verify | codes | done
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedSecret, setCopiedSecret] = useState(false);
  // Iter 155.1 · Defer the scoped→full-session token swap until the user
  // clicks Continue on the backup-codes screen. If we swap immediately
  // inside handleVerify, the mfaSetupToken redirect effect below would
  // unmount the codes phase before the user could copy or download them.
  const [pendingFullSession, setPendingFullSession] = useState(null);
  // Snapshot the identity we need for the download filename and the
  // "Signed in as" footer BEFORE the token swap clears mfaSetupUser.
  const [displayEmail, setDisplayEmail] = useState(null);
  const [displayRole, setDisplayRole] = useState(null);

  const email = displayEmail ?? mfaSetupUser?.email;
  const role = displayRole ?? mfaSetupUser?.role;

  // Iter 155 · If a user hits /mfa-setup directly without a scoped
  // token stashed, bounce them to /auth — no reason to be here.
  // Iter 155.1 · BUT if we've already flipped to the codes phase (we
  // just cleared the scoped token as part of a successful enroll +
  // upgrade), stay put so the user can save their backup codes.
  useEffect(() => {
    if (!mfaSetupToken && phase !== 'codes' && phase !== 'done' && !pendingFullSession) {
      navigate('/auth', { replace: true });
    }
  }, [mfaSetupToken, phase, pendingFullSession, navigate]);

  const beginSetup = useCallback(async () => {
    if (!mfaSetupToken) return;
    setPhase('loading');
    try {
      const { data } = await axios.post(
        `${API}/auth/mfa/setup`,
        {},
        getMfaSetupAuthHeader(),
      );
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setPhase('setup');
    } catch (err) {
      const raw = err?.response?.data?.detail;
      toast.error(typeof raw === 'string' ? raw : 'Could not start two-factor setup.');
      setPhase('setup');
    }
  }, [mfaSetupToken, getMfaSetupAuthHeader]);

  useEffect(() => { beginSetup(); }, [beginSetup]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim() || code.trim().length < 6) {
      toast.error('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/mfa/enable`,
        { code: code.trim() },
        getMfaSetupAuthHeader(),
      );
      // Iter 155.1 · Snapshot identity BEFORE the token swap so the
      // download filename + footer keep rendering after we drop the
      // scoped token.
      setDisplayEmail(mfaSetupUser?.email || null);
      setDisplayRole(mfaSetupUser?.role || null);
      setBackupCodes(data.backup_codes || []);
      // Defer the scoped → full-session swap until the user clicks
      // Continue. We just hold the returned {token, user} here.
      if (data.token && data.user) {
        setPendingFullSession({ token: data.token, user: data.user });
      }
      setPhase('codes');
      toast.success('Two-factor authentication is on.');
    } catch (err) {
      const raw = err?.response?.data?.detail;
      toast.error(typeof raw === 'string' ? raw : 'That code didn’t match. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 1500);
    } catch {
      toast.error('Couldn’t copy — long-press to select manually.');
    }
  };

  const onDownloadBackup = () => {
    const body = [
      'CaraLoom · Two-factor backup codes',
      `Account: ${email || ''}`,
      `Issued : ${new Date().toISOString()}`,
      '',
      'Each code can be used ONCE if you lose access to your authenticator app.',
      'Store them somewhere safe — a password manager or printed and locked away.',
      '',
      ...backupCodes.map((c, i) => `${(i + 1).toString().padStart(2, ' ')}. ${c}`),
    ].join('\n');
    const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `caraloom-backup-codes-${(email || 'account').replace(/[^\w.-]+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onFinish = () => {
    // Iter 155.1 · commit the scoped → full-session swap now that the
    // user has had a chance to save their backup codes.
    if (pendingFullSession) {
      completeMfaSetup(pendingFullSession);
    }
    const home = ROLE_HOME[role] || '/';
    navigate(home, { replace: true });
  };

  const onSignOut = async () => {
    await logout();
    navigate('/auth', { replace: true });
  };

  const heading = useMemo(() => {
    if (phase === 'codes') return 'Save your backup codes';
    return 'Set up two-factor authentication';
  }, [phase]);

  return (
    <div
      className="min-h-screen bg-cn-bg flex items-center justify-center p-4 sm:p-6"
      data-testid="mfa-setup-page"
    >
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <CaraLoomLogo />
          <button
            type="button"
            onClick={onSignOut}
            className="inline-flex items-center gap-1.5 text-sm text-cn-text-muted hover:text-cn-primary transition-colors"
            data-testid="mfa-setup-sign-out"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="cn-card p-6 sm:p-8 space-y-6">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-cn-primary/10 p-2.5 flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-cn-primary" />
            </div>
            <div>
              <h1
                className="font-display text-xl sm:text-2xl font-semibold text-cn-text"
                data-testid="mfa-setup-heading"
              >
                {heading}
              </h1>
              <p className="text-sm text-cn-text-muted mt-1">
                {phase === 'codes' ? (
                  <>Save these one-time backup codes somewhere safe. Each can be used exactly once if you lose your authenticator app.</>
                ) : (
                  <>Your role ({role || 'this account'}) protects patient data, so two-factor authentication is required before you can continue.</>
                )}
              </p>
            </div>
          </div>

          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8 gap-2" data-testid="mfa-setup-loading">
              <Loader2 className="w-6 h-6 text-cn-primary animate-spin" />
              <p className="text-sm text-cn-text-muted">Preparing your authenticator link…</p>
            </div>
          )}

          {phase === 'setup' && (
            <form onSubmit={handleVerify} className="space-y-5" data-testid="mfa-setup-form">
              <ol className="space-y-4 text-sm text-cn-text">
                <li className="flex gap-3">
                  <span className="rounded-full bg-cn-bg-soft w-6 h-6 flex items-center justify-center text-xs font-semibold text-cn-primary flex-shrink-0">1</span>
                  <div>
                    <p className="font-medium">Open your authenticator app.</p>
                    <p className="text-cn-text-muted text-xs mt-0.5">
                      1Password, Google Authenticator, Authy, Microsoft Authenticator — anything TOTP.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="rounded-full bg-cn-bg-soft w-6 h-6 flex items-center justify-center text-xs font-semibold text-cn-primary flex-shrink-0">2</span>
                  <div className="w-full">
                    <p className="font-medium">Scan this QR code, or paste the key.</p>
                    <div className="mt-3 rounded-lg border border-cn-border bg-white p-3 flex flex-col sm:flex-row items-center gap-4">
                      {qrCode ? (
                        <img
                          src={qrCode}
                          alt="Two-factor QR code"
                          className="w-40 h-40 rounded"
                          data-testid="mfa-setup-qr"
                        />
                      ) : (
                        <div className="w-40 h-40 flex items-center justify-center rounded bg-cn-bg-soft">
                          <Loader2 className="w-5 h-5 text-cn-text-muted animate-spin" />
                        </div>
                      )}
                      <div className="flex-1 w-full">
                        <div className="text-xs text-cn-text-muted mb-1">Manual key</div>
                        <div className="flex items-center gap-2">
                          <code
                            className="text-xs font-mono bg-cn-bg-soft px-2 py-1.5 rounded flex-1 break-all"
                            data-testid="mfa-setup-secret"
                          >
                            {secret || '…'}
                          </code>
                          <button
                            type="button"
                            onClick={onCopySecret}
                            className="p-1.5 rounded hover:bg-cn-bg-soft text-cn-text-muted"
                            aria-label="Copy manual key"
                            data-testid="mfa-setup-copy-secret"
                          >
                            {copiedSecret ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="rounded-full bg-cn-bg-soft w-6 h-6 flex items-center justify-center text-xs font-semibold text-cn-primary flex-shrink-0">3</span>
                  <div className="w-full">
                    <p className="font-medium">Enter the 6-digit code your app shows.</p>
                    <div className="mt-3 flex items-center gap-3">
                      <KeyRound className="w-4 h-4 text-cn-text-muted flex-shrink-0" />
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={9}
                        placeholder="123456"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
                        className="h-12 text-center text-xl tracking-[0.4em] font-mono"
                        data-testid="mfa-setup-code"
                        autoFocus
                      />
                    </div>
                  </div>
                </li>
              </ol>

              <Button
                type="submit"
                disabled={submitting || code.length < 6}
                className="cn-btn cn-btn-primary w-full h-11"
                data-testid="mfa-setup-verify"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying…
                  </>
                ) : (
                  <>Verify & enable</>
                )}
              </Button>
            </form>
          )}

          {phase === 'codes' && (
            <div className="space-y-5" data-testid="mfa-setup-backup-codes">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                Two-factor authentication is enabled on your account.
              </div>

              <div className="rounded-lg border border-cn-border bg-cn-bg-soft p-4">
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {backupCodes.map((c) => (
                    <div key={c} className="bg-white rounded px-2 py-1.5 text-center" data-testid="mfa-backup-code-row">
                      {c}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  onClick={onDownloadBackup}
                  className="cn-btn cn-btn-outline flex-1 h-11"
                  data-testid="mfa-setup-download"
                >
                  <Download className="w-4 h-4 mr-2" /> Download .txt
                </Button>
                <Button
                  type="button"
                  onClick={onFinish}
                  className="cn-btn cn-btn-primary flex-1 h-11"
                  data-testid="mfa-setup-continue"
                >
                  Continue to CaraLoom <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <p className="text-xs text-cn-text-muted text-center">
                You can regenerate these anytime from Privacy dashboard → Account security.
              </p>
            </div>
          )}
        </div>

        <p className="mt-4 text-xs text-cn-text-muted text-center">
          Signed in as <span className="font-medium text-cn-text">{email || '—'}</span>
        </p>
      </div>
    </div>
  );
}
